import { Truck, BlogPost } from '../types';

export interface ChatMessage {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
    isAction?: boolean;
    actionType?: 'whatsapp_link';
}

export type Language = 'en' | 'sw' | 'fr' | 'es' | 'ar';

export const INITIAL_GREETING = "Hello! Welcome to Automax Co. Ltd. How can I assist you today? (English | Kiswahili | Français | Español | العربية)";

interface Translation {
    greetings: string[];
    fallback: string[];
    handover: string;
    handover_reason_price: string;
    handover_reason_complex: string;
    qualifying: {
        type: string;
        usage: string;
    };
    answers: {
        [key: string]: string;
    };
    prompts: {
        details: string;
        financing: string;
        quote: string;
    };
}

const TRANSLATIONS: Record<Language, Translation> = {
    en: {
        greetings: ["Hello! How can I help you?", "Hi there! Welcome to Automax.", "Greetings! How may I assist you?"],
        fallback: ["I see. Could you tell me more?", "Interesting. Please go on.", "I understand."],
        handover: "I'd like to connect you with a specialist on WhatsApp for more details.",
        handover_reason_price: "The price depends on the specific configuration and delivery location. I can generate a preliminary estimate or connect you with sales for a formal quote. Would you like to discuss the specs first?",
        handover_reason_complex: "That sounds specific. Let me connect you with our technical team on WhatsApp for a detailed answer.",
        qualifying: {
            type: "To serve you best, are you looking for a Truck, Trailer, or Spare Parts?",
            usage: "What will this be used for? (e.g., Construction, Logistics, Mining)"
        },
        answers: {
            location: "We are located at Noble Center Oysterbay, Dar Es Salaam, Tanzania.",
            warranty: "We offer comprehensive warranties: 2 years for Shacman Trucks, 1 year for Tippers, and 6 months for Trailers.",
            shacman: "We supply Shacman H3000, X3000, and X6000 models with Weichai engines.",
            cimc: "Our CIMC trailers come with FUWA, CIMC, or BPW axles and up to 32 tons capacity.",
            finance: "We accept Cash, Letters of Credit, and Bank Financing (CRDB, NMB, NBC).",
            delivery: "Local stock is ready in 2-4 days. Orders from China take 30-60 days.",
            parts: "We have a $2M spare parts stock at our Nyerere Road workshop."
        },
        prompts: {
            details: "Would you like to know about its engine, capacity, or warranty?",
            financing: "We can also discuss financing options if you're interested.",
            quote: "Should I prepare a quote request for you?"
        }
    },
    sw: {
        greetings: ["Habari! Karibu Automax. Nikusaidieaje?", "Hujambo! Karibu sana.", "Habari yako! Uko tayari kwa huduma bora?"],
        fallback: ["Sawa, nieleze zaidi.", "Naelewa. Tafadhali endelea.", "Sawa kabisa."],
        handover: "Ningependa kukuunganisha na mtaalamu wetu WhatsApp kwa maelezo zaidi.",
        handover_reason_price: "Bei inategemea vipimo na mahali pa kufikisha. Naweza kukupa makadirio au kukuunganisha na mauzo. Je, tujadili vipimo kwanza?",
        handover_reason_complex: "Hilo linahitaji ufafanuzi wa kina. Acha nikuunganishe na timu yetu ya ufundi WhatsApp.",
        qualifying: {
            type: "Je, unatafuta Lori, Trela, au Vipuri?",
            usage: "Gari hili litatumika kwa kazi gani? (mfano: Ujenzi, Usafirishaji, Migodi)"
        },
        answers: {
            location: "Tunapatikana Noble Center Oysterbay, Dar Es Salaam, Tanzania.",
            warranty: "Tunatoa udhamini: Miaka 2 kwa Shacman, Mwaka 1 kwa Tippers, na Miezi 6 kwa Trela.",
            shacman: "Tunauza Shacman H3000, X3000, na X6000 zenye injini za Weichai.",
            cimc: "Trela zetu za CIMC zina ekseli za FUWA, CIMC, au BPW na uwezo wa tani 32.",
            finance: "Tunapokea Pesa Taslimu na Mikopo ya Benki (CRDB, NMB, NBC).",
            delivery: "Stoku iliyopo inatoka kwa siku 2-4. Oda kutoka China huchukua siku 30-60.",
            parts: "Tuna stoku ya vipuri ya thamani ya $2M katika karakana yetu ya Nyerere Road."
        },
        prompts: {
            details: "Je, ungependa kujua kuhusu injini, uwezo, au udhamini?",
            financing: "Tunaweza pia kujadili chaguzi za mkopo kama utapenda.",
            quote: "Je, nikuandalie ombi la bei?"
        }
    },
    fr: {
        greetings: ["Bonjour! Bienvenue chez Automax.", "Salut! Comment puis-je vous aider?", "Bonjour! Heureux de vous voir."],
        fallback: ["Je vois. Dites-m'en plus.", "D'accord, continuez s'il vous plaît.", "Je comprends."],
        handover: "Je voudrais vous mettre en contact avec un spécialiste sur WhatsApp.",
        handover_reason_price: "Le prix dépend de la configuration. Je peux vous donner une estimation ou vous connecter aux ventes. Voulez-vous discuter des spécifications d'abord ?",
        handover_reason_complex: "C'est une question technique. Laissez-moi vous connecter avec notre équipe sur WhatsApp.",
        qualifying: {
            type: "Cherchez-vous un camion, une remorque ou des pièces détachées?",
            usage: "Quelle sera l'utilisation principale? (ex: Construction, Logistique, Mines)"
        },
        answers: {
            location: "Nous sommes situés au Noble Center Oysterbay, Dar Es Salaam, Tanzanie.",
            warranty: "Garantie: 2 ans pour Camions Shacman, 1 an pour Tippers, 6 mois pour Remorques.",
            shacman: "Nous fournissons les modèles Shacman H3000, X3000 et X6000.",
            cimc: "Nos remorques CIMC ont des essieux FUWA, CIMC ou BPW.",
            finance: "Nous acceptons les paiements comptants et le financement bancaire.",
            delivery: "Le stock local est prêt en 2-4 jours. Les commandes de Chine prennent 30-60 jours.",
            parts: "Nous avons un stock de pièces détachées de 2M$ à notre atelier."
        },
        prompts: {
            details: "Voulez-vous savoir sur le moteur, la capacité ou la garantie ?",
            financing: "Nous pouvons également discuter des options de financement.",
            quote: "Dois-je préparer une demande de devis pour vous ?"
        }
    },
    es: {
        greetings: ["¡Hola! Bienvenido a Automax.", "¿Qué tal? ¿En qué puedo ayudarte?", "¡Saludos! Estamos aquí para servirte."],
        fallback: ["Entiendo. Cuéntame más.", "Ya veo. Por favor continúa.", "Comprendo."],
        handover: "Me gustaría conectarte con un especialista en WhatsApp para más detalles.",
        handover_reason_price: "El precio depende de la configuración. Puedo darte una estimación o conectarte con ventas. ¿Quieres discutir las especificaciones primero?",
        handover_reason_complex: "Esa es una pregunta técnica. Déjame conectarte con nuestro equipo en WhatsApp.",
        qualifying: {
            type: "¿Buscas un Camión, un Remolque o Repuestos?",
            usage: "¿Para qué se utilizará? (ej: Construcción, Logística, Minería)"
        },
        answers: {
            location: "Estamos en Noble Center Oysterbay, Dar Es Salaam, Tanzania.",
            warranty: "Garantía: 2 años Camiones Shacman, 1 año Volquetes, 6 meses Remolques.",
            shacman: "Suministramos modelos Shacman H3000, X3000 y X6000.",
            cimc: "Nuestros remolques CIMC tienen ejes FUWA, CIMC o BPW.",
            finance: "Aceptamos efectivo y financiamiento bancario.",
            delivery: "Stock local listo en 2-4 días. Pedidos de China tardan 30-60 días.",
            parts: "Tenemos un stock de repuestos de $2M en nuestro taller."
        },
        prompts: {
            details: "¿Quiere saber sobre el motor, la capacidad o la garantía?",
            financing: "También podemos discutir opciones de financiamiento.",
            quote: "¿Debo preparar una solicitud de cotización para usted?"
        }
    },
    ar: {
        greetings: ["مرحباً! أهلاً بك في أوتوماكس.", "أهلاً وسهلاً! كيف يمكنني مساعدتك؟", "السلام عليكم! نحن هنا لخدمتك."],
        fallback: ["أفهمك. أخبرني المزيد.", "حسناً، أكمل من فضلك.", "فهمت."],
        handover: "أود ربطك بمتخصص عبر واتساب لمزيد من التفاصيل.",
        handover_reason_price: "السعر يعتمد على المواصفات. يمكنني تقديم تقدير أو ربطك بالمبيعات. هل نناقش المواصفات أولاً؟",
        handover_reason_complex: "هذا سؤال تقني. دعني أوصلك بفريقنا الفني عبر واتساب.",
        qualifying: {
            type: "هل تبحث عن شاحنة، مقطورة، أو قطع غيار؟",
            usage: "ما هو الاستخدام المخطط له؟ (مثال: البناء، الخدمات اللوجستية، التعدين)"
        },
        answers: {
            location: "نحن نقع في نوبل سنتر، أويسترباي، دار السلام، تنزانيا.",
            warranty: "الضمان: سنتان لشاحنات شاكمان، سنة للقلابات، و6 أشهر للمقطورات.",
            shacman: "نحن نوفر موديلات شاكمان H3000 و X3000 و X6000.",
            cimc: "مقطورات CIMC لدينا تأتي بمحاور FUWA أو CIMC أو BPW.",
            finance: "نقبل الدفع النقدي والتمويل البنكي.",
            delivery: "المخزون المحلي جاهز خلال 2-4 أيام. الطلبات من الصين تستغرق 30-60 يوماً.",
            parts: "لدينا مخزون قطع غيار بقيمة 2 مليون دولار في ورشتنا."
        },
        prompts: {
            details: "هل تود معرفة المحرك، السعة أو الضمان؟",
            financing: "يمكننا أيضاً مناقشة خيارات التمويل.",
            quote: "هل أقوم بإعداد طلب عرض سعر لك؟"
        }
    }
};

const KEYWORDS_TO_LANGUAGE: Record<string, Language> = {
    'habari': 'sw', 'jambo': 'sw', 'shikamoo': 'sw', 'mambo': 'sw', 'bei': 'sw', 'asante': 'sw',
    'bonjour': 'fr', 'salut': 'fr', 'prix': 'fr', 'merci': 'fr', 'oui': 'fr',
    'hola': 'es', 'mundo': 'es', 'gracias': 'es', 'precio': 'es', 'si': 'es',
    'salam': 'ar', 'marhaba': 'ar', 'shukran': 'ar', 'kayfa': 'ar', 'naam': 'ar'
};

const KEYWORDS_MAPPING: Record<string, string> = {
    // High priority specific terms
    'price': 'price', 'cost': 'price', 'quote': 'price', 'bei': 'price', 'gharama': 'price', 'prix': 'price', 'coût': 'price', 'precio': 'price', 'costo': 'price', 'سعر': 'price',
    'location': 'location', 'where': 'location', 'wapi': 'location', 'mahali': 'location', 'où': 'location', 'donde': 'location', 'مكان': 'location', 'اين': 'location',

    // Inventory Categories
    'shacman': 'shacman', 'truck': 'shacman', 'lori': 'shacman', 'camion': 'shacman', 'camión': 'shacman', 'شاحنة': 'shacman',
    'trailer': 'cimc', 'cimc': 'cimc', 'trela': 'cimc', 'remorque': 'cimc', 'remolque': 'cimc', 'مقطورة': 'cimc',

    // Core Business Services
    'finance': 'finance', 'loan': 'finance', 'mikopo': 'finance', 'financement': 'finance', 'financiamiento': 'finance', 'تمويل': 'finance',
    'financing': 'finance', 'credit': 'finance', 'bank': 'finance',

    'delivery': 'delivery', 'shipping': 'delivery', 'usafiri': 'delivery', 'livraison': 'delivery', 'entrega': 'delivery', 'توصيل': 'delivery', 'شحن': 'delivery',
    'parts': 'parts', 'spare': 'parts', 'vipuri': 'parts', 'pièces': 'parts', 'repuestos': 'parts', 'قطع': 'parts',

    'contact': 'contact', 'email': 'contact', 'phone': 'contact', 'call': 'contact',
    'about': 'about', 'mission': 'about', 'history': 'about', 'who are': 'about',

    'service': 'services', 'maintenance': 'services', 'repair': 'services', 'workshop': 'services'
};

// --- EXTENDED KNOWLEDGE BASE ---

const FAQ_DATABASE = [
    {
        keywords: ['warranty', 'guarantee', 'period'],
        answer: "We stand behind our vehicles with comprehensive warranty coverage: **2 years** for Shacman Trucks, **1 year** for Tippers, and **6 months** for Trailers. This coverage includes manufacturer defects and major component failures. Additionally, we maintain a **$2 million monthly spare parts inventory** at our Nyerere Road workshop, ensuring immediate parts availability and minimizing your downtime. Our service team is always ready to support you throughout the warranty period and beyond."
    },
    {
        keywords: ['document', 'requirement', 'paperwork', 'application', 'tin', 'audit'],
        answer: "To help you secure bank financing smoothly, here's what you'll typically need to prepare:\n\n**1. Company Documents:**\n   - Certificate of Incorporation\n   - Tax Identification Number (TIN)\n   - Valid Business License\n\n**2. Financial Records:**\n   - 3 years of audited financial accounts\n   - 12 months of recent bank statements\n\n**3. Purchase Documentation:**\n   - A Proforma Invoice from Automax (we'll provide this)\n\nOur sales team works closely with CRDB, NMB, NBC, and NCBA banks to expedite your financing application. We can guide you through the entire process."
    },
    {
        keywords: ['deposit', 'down payment', 'install', 'advance'],
        answer: "The minimum deposit for financing is 20%. The exact amount depends on the vehicle price and bank approval."
    },
    {
        keywords: ['internal credit', 'loan from you', 'credit from automax'],
        answer: "Automax does NOT offer internal credit except under rare special circumstances. We partner with major banks like CRDB, NMB, and NBC to help you get financing."
    },
    {
        keywords: ['delivery time', 'how long', 'wait', 'shipping time'],
        answer: "Our delivery timelines are designed to get you operational as quickly as possible:\n\n**Local Stock:** If the vehicle is in our Dar es Salaam inventory, we can complete registration and delivery within **2-4 days**. This includes all necessary paperwork and compliance checks.\n\n**China Orders:** For vehicles ordered directly from our manufacturing partners:\n   - Standard Trucks & Flatbeds: **30-60 days**\n   - Specialized Tankers: **Up to 75 days**\n\nThese timelines include manufacturing, shipping, customs clearance, and final preparation. We'll keep you updated at every stage of the process."
    },
    {
        keywords: ['clearance', 'registration', 'plate'],
        answer: "For local stock, registration takes just 2-4 days. You'll need your TIN, ID/Passport, and valid insurance."
    },
    {
        keywords: ['spec', 'engine', 'gearbox', 'axle', 'horsepower'],
        answer: "Our Shacman trucks feature powerful Weichai engines and FAST gearboxes. Our CIMC trailers use reliable FUWA, CIMC, or BPW axles."
    },
    {
        keywords: ['gps', 'track', 'security'],
        answer: "We can link you with verified partners for GPS tracking, fleet management, and fuel monitoring systems to keep your assets safe."
    },
    {
        keywords: ['contact', 'reach', 'phone', 'email', 'address', 'office'],
        answer: "You can reach us at:\n\n• **📍 Address**: Noble Center Oysterbay, Dar Es Salaam, Tanzania\n• **📞 Phone**: +255 716 333 11\n• **📧 Email**: sales@automaxspace.com\n• **WhatsApp**: +255 754 655 551\n\nOr visit our Contact page on the website to send a message directly! ✅"
    },
    {
        keywords: ['promo', 'offer', 'discount', 'deal', 'special'],
        answer: "We often have special promotions running! Please check our homepage for the latest offers, or I can connect you with our sales team on WhatsApp for current deals. 🔥"
    },
    {
        keywords: ['service', 'maintenance', 'repair', 'workshop'],
        answer: "We offer 4 core services:\n\n• **Fleet Maintenance** — Comprehensive scheduled maintenance across East Africa 🔧\n• **Genuine Parts** — OEM parts with 48-hour regional delivery ⚙️\n• **Warranty Support** — Extended warranty packages in 5 countries 🛡️\n• **Export Logistics** — Complete cross-border purchase handling 🌍\n\nVisit our Services page for more details!"
    },
    {
        keywords: ['hour', 'open', 'close', 'time', 'working'],
        answer: "Our business hours are:\n• **Monday - Friday**: 08:00 - 17:00\n• **Saturday**: 09:00 - 13:00\n• **Sunday**: Closed\n\nYou can always reach us on WhatsApp outside office hours! 📱"
    },
    {
        keywords: ['website', 'page', 'browse', 'inventory', 'calculator'],
        answer: "You can explore our website for more information:\n• **Inventory** — Browse all trucks and trailers\n• **Financing** — Use our financing calculator\n• **Services** — Learn about our support services\n• **Blog** — Read industry news and tips\n• **About** — Our company story and mission"
    }
];

const SMALL_TALK: Record<string, string[]> = {
    'greeting': ["Hello!", "Hi there!", "Good day!", "Welcome back."],
    'thanks': ["You're very welcome!", "Happy to help!", "Anytime!", "Karibu sana."],
    'identity': ["I am the Automax Virtual Assistant, here to help you find the best trucks and trailers.", "I'm a digital assistant powered by Automax, ready to answer your questions."],
    'bot_check': ["I am a virtual assistant, but I can connect you with a real human on WhatsApp anytime!", "I'm a bot by code, but I'm here to help like a pro."],
    'bye': ["Goodbye! Have a great day.", "Kwa heri! See you soon.", "Safe travels!"]
};

// --- SEARCH FUNCTIONS ---

export interface BotResponse {
    text: string;
    shouldHandover: boolean;
    detectedLanguage?: Language;
}

export const detectLanguage = (input: string, currentLanguage: Language = 'en'): Language => {
    const lowerInput = input.toLowerCase();
    const words = lowerInput.split(/\s+/);
    for (const word of words) {
        const cleanWord = word.replace(/[.,?!]/g, '');
        if (KEYWORDS_TO_LANGUAGE[cleanWord]) return KEYWORDS_TO_LANGUAGE[cleanWord];
    }
    return currentLanguage;
};

const searchInventory = (query: string, trucks: Truck[], language: Language): string | null => {
    const lowerQuery = query.toLowerCase();
    const t = TRANSLATIONS[language];

    // Extract meaningful tokens (remove common filler words)
    const stopWords = ['tell', 'me', 'about', 'the', 'show', 'find', 'do', 'you', 'have', 'any', 'what', 'is', 'a', 'an', 'for', 'looking', 'want', 'need'];
    const queryTokens = lowerQuery.split(/\s+/).filter(token =>
        token.length > 2 && !stopWords.includes(token)
    );

    // Score each truck based on token matches
    const scoredTrucks = trucks.map(truck => {
        const name = truck.name.toLowerCase();
        const cat = truck.category.toLowerCase();
        const brand = truck.brand.toLowerCase();

        let score = 0;

        // Check each query token against truck fields
        queryTokens.forEach(token => {
            if (name.includes(token)) score += 5;
            if (cat.includes(token)) score += 3;
            if (brand.includes(token)) score += 4;
        });

        // Also check if the entire query is contained in name (direct match boost)
        if (name.includes(lowerQuery)) score += 10;

        return { truck, score };
    });

    // Filter trucks with score > 0 and sort by score
    const available = scoredTrucks
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ truck }) => truck);

    if (available.length > 0) {
        // Pick the top match for detailed response
        if (available.length === 1 || (available[0] && queryTokens.length > 0)) {
            const truck = available[0];
            const details = [
                truck.year ? `${truck.year}` : '',
                truck.engine ? `${truck.engine}` : '',
                truck.horsepower ? `${truck.horsepower}HP` : '',
                truck.transmission ? `${truck.transmission}` : '',
                truck.location ? `located in ${truck.location}` : ''
            ].filter(Boolean).join(", ");

            return `We have the ${truck.name} available. It's a ${details}. ${t.prompts.details}`;
        }

        // General list for multiple matches
        const uniqueModels = Array.from(new Set(available.slice(0, 3).map(t => t.name)));
        return `Yes, we have ${available.length} unit(s) matching your request. Models include: ${uniqueModels.join(", ")}. ${t.prompts.details}`;
    }
    return null;
};

const searchBlogs = (query: string, posts: BlogPost[]): string | null => {
    const lowerQuery = query.toLowerCase();
    const relevant = posts.filter(p =>
        lowerQuery.includes(p.title.toLowerCase()) ||
        p.tags.some(tag => lowerQuery.includes(tag.toLowerCase()))
    );
    if (relevant.length > 0) {
        return `I found an article: "${relevant[0].title}". You can read it in our Blog section.`;
    }
    return null;
};

const searchKnowledgeBase = (query: string): string | null => {
    const lowerQuery = query.toLowerCase();

    // Check FAQ Database
    for (const item of FAQ_DATABASE) {
        if (item.keywords.some(k => lowerQuery.includes(k))) {
            return item.answer;
        }
    }
    return null;
};

const handleSmallTalk = (query: string): string | null => {
    const lower = query.toLowerCase();
    if (lower.match(/\b(thank|thanks|shukrani|asante)\b/)) return SMALL_TALK['thanks'][Math.floor(Math.random() * SMALL_TALK['thanks'].length)];
    if (lower.match(/\b(hi|hello|hey|greetings|mambo|vip)\b/)) return SMALL_TALK['greeting'][Math.floor(Math.random() * SMALL_TALK['greeting'].length)];
    if (lower.match(/\b(who are you|your name|what are you)\b/)) return SMALL_TALK['identity'][0];
    if (lower.match(/\b(human|real person|robot|bot)\b/)) return SMALL_TALK['bot_check'][0];
    if (lower.match(/\b(bye|goodbye|later)\b/)) return SMALL_TALK['bye'][0];
    return null;
};

export const getBotResponse = (
    input: string,
    interactionCount: number,
    currentLanguage: Language = 'en',
    trucks: Truck[] = [],
    posts: BlogPost[] = []
): BotResponse => {
    const language = detectLanguage(input, currentLanguage);
    const t = TRANSLATIONS[language];
    const lowerInput = input.toLowerCase();

    // 1. Small Talk Match
    const smallTalk = handleSmallTalk(lowerInput);
    if (smallTalk) {
        return { text: smallTalk, shouldHandover: false, detectedLanguage: language };
    }

    // 2. High Priority Keyword Intents (Price, etc - defined in MAPPING)
    let intent: string | null = null;
    let longestMatchLen = 0;
    for (const [key, mappedIntent] of Object.entries(KEYWORDS_MAPPING)) {
        if (lowerInput.includes(key)) {
            if (key.length > longestMatchLen) {
                intent = mappedIntent;
                longestMatchLen = key.length;
            }
        }
    }

    // Soft Handover logic for Price
    if (intent === 'price') {
        return { text: t.handover_reason_price, shouldHandover: false, detectedLanguage: language };
    }

    // 3. Inventory & Blogs (Dynamic)
    if (lowerInput.length > 2) {
        const inv = searchInventory(lowerInput, trucks, language);
        if (inv) return { text: inv, shouldHandover: false, detectedLanguage: language };

        const blog = searchBlogs(lowerInput, posts);
        if (blog) return { text: blog, shouldHandover: false, detectedLanguage: language };
    }

    // 4. Extended Knowledge Base (FAQ)
    const faq = searchKnowledgeBase(lowerInput);
    if (faq) return { text: faq, shouldHandover: false, detectedLanguage: language };

    // 5. Static Content Lookup (About, Services, etc via Intent)
    if (intent && t.answers[intent]) {
        return { text: t.answers[intent], shouldHandover: false, detectedLanguage: language };
    }

    // 6. Conversational Flow - Try to help more before handing over
    if (interactionCount === 0) return { text: t.qualifying.type, shouldHandover: false, detectedLanguage: language };
    if (interactionCount === 1) return { text: t.qualifying.usage, shouldHandover: false, detectedLanguage: language };

    // Provide helpful suggestions before fallback
    if (interactionCount < 8) {
        const helpfulPrompts = [
            "I can help you with information about our trucks, trailers, financing options, warranty details, or delivery timelines. What would you like to know more about?",
            "Let me know if you'd like to explore our vehicle specifications, financing packages, warranty coverage, or delivery options. I'm here to help!",
            "I can provide detailed information on our inventory, payment plans, after-sales support, or technical specifications. What interests you most?",
            "Feel free to ask about our vehicle features, pricing estimates, financing requirements, or service packages. How can I assist you further?"
        ];
        const promptIndex = Math.min(interactionCount - 2, helpfulPrompts.length - 1);
        return {
            text: helpfulPrompts[Math.max(0, promptIndex)],
            shouldHandover: false,
            detectedLanguage: language
        };
    }

    // 7. Fallback only after several interactions
    return { text: t.handover_reason_complex, shouldHandover: true, detectedLanguage: language };
};
